import { useState, type FormEvent } from 'react'
import { useCreateFacility } from '../index.js'
import type { Facility } from '../index.js'
import { Modal } from '../../core/index.js'
import { NewWorkerModal } from '../../roster/index.js'
import { Field, slugId } from '../../core/index.js'
import { TimeZoneSelect, browserTimeZone } from './TimeZoneSelect.js'
import { EMPTY_ADDRESS, addressFromDraft, formatAddress, hasAddressContent, missingAddressFields, type AddressDraft } from './facilityAddress.js'
import { Button } from '../../core/index.js'
import { FacilityIcon, WorkerIcon } from '../../core/index.js'
import { CountryInput, PhoneInput, RegionInput, regionLabelOf } from '../../core/index.js'

const INPUT_CLASS = 'w-full rounded border border-marine/30 bg-white px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:border-marine focus:ring-2 focus:ring-marine/20'
const FORM_ID = 'new-facility-form'
/** The API's own rule for a facilityId. */
const FACILITY_ID_PATTERN = '[A-Za-z0-9-]+'

interface FacilityForm {
  name: string
  /** Null until the operator edits it; until then it follows the name. */
  facilityIdOverride: string | null
  timezone: string
  address: AddressDraft
  phone: string
}

function emptyForm(): FacilityForm {
  return { name: '', facilityIdOverride: null, timezone: browserTimeZone() ?? '', address: EMPTY_ADDRESS, phone: '' }
}

interface NewFacilityModalProps {
  clientId: string | undefined
  organizationName: string | undefined
  onClose: () => void
}

/**
 * An operator registering a new facility: its id, name and time zone, and
 * its address if known - every field of the record, since a facility is
 * the operator's to create and keep (SeeWhoCares's admin can only look).
 * Once one is saved the next is offered straight away.
 */
export function NewFacilityModal({ clientId, organizationName, onClose }: NewFacilityModalProps) {
  const mutation = useCreateFacility(clientId)
  const [form, setForm] = useState<FacilityForm>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [created, setCreated] = useState<Facility | null>(null)
  const [isAddingWorker, setIsAddingWorker] = useState(false)
  const [workersAdded, setWorkersAdded] = useState(0)

  const facilityId = form.facilityIdOverride ?? slugId(form.name)

  function update(changes: Partial<FacilityForm>): void {
    setForm((current) => ({ ...current, ...changes }))
  }

  function updateAddress(changes: Partial<AddressDraft>): void {
    setForm((current) => ({ ...current, address: { ...current.address, ...changes } }))
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (hasAddressContent(form.address)) {
      const missing = missingAddressFields(form.address)
      if (missing.length > 0) {
        setValidationError(`The address still needs: ${missing.join(', ')}. Clear every address field to leave it for later.`)
        return
      }
    }
    setValidationError(null)
    const facility = await mutation.mutateAsync({
      facilityId,
      name: form.name.trim(),
      ...(form.timezone === '' ? {} : { timezone: form.timezone }),
      ...(hasAddressContent(form.address) ? { address: addressFromDraft(form.address) } : {}),
      ...(form.phone.trim() === '' ? {} : { phone: form.phone.trim() }),
    })
    setCreated(facility)
  }

  function createAnother(): void {
    setCreated(null)
    setWorkersAdded(0)
    mutation.reset()
    setForm(emptyForm())
  }

  const canSubmit = Boolean(clientId) && form.name.trim() !== '' && facilityId !== '' && !mutation.isPending
  const error = validationError ?? (mutation.isError ? mutation.error.message : null)

  return (
    <>
    <Modal
      eyebrow="New facility"
      title={created ? 'Facility created' : 'Register a facility'}
      subtitle={organizationName}
      // With the worker form open on top, Escape and backdrop clicks belong to it.
      canClose={!mutation.isPending && !isAddingWorker}
      onClose={onClose}
      footer={
        created ? (
          <div className="flex flex-wrap items-center gap-4">
            <Button icon={WorkerIcon} autoFocus onClick={() => setIsAddingWorker(true)}>
              {workersAdded === 0 ? `Add workers to ${created.name}` : `Add more workers to ${created.name}`}
            </Button>
            <Button icon={FacilityIcon} variant="secondary" onClick={createAnother}>
              Create another facility
            </Button>
            <button type="button" onClick={onClose} className="text-xs font-bold text-marine hover:text-marine-dk">
              Done
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <Button icon={FacilityIcon} type="submit" form={FORM_ID} disabled={!canSubmit}>
              {mutation.isPending ? 'Creating…' : 'Create facility'}
            </Button>
            <button type="button" onClick={onClose} disabled={mutation.isPending} className="text-xs font-bold text-marine hover:text-marine-dk disabled:opacity-40">
              Cancel
            </button>
          </div>
        )
      }
    >
      {created ? (
        <div className="bg-canvas p-6 text-sm text-ink space-y-2">
          <p>
            <span className="font-bold">{created.name}</span> is registered
            {created.timezone ? ` in the ${created.timezone.replaceAll('_', ' ')} zone` : ', reckoned in UTC'}
            {created.address ? ` at ${formatAddress(created.address)}` : ', with no address yet'}.
          </p>
          <p className="text-xs text-ink-mid">
            {workersAdded === 0 ? 'It has no workers yet: add them here one at a time, or by a roster import.' : `${workersAdded} ${workersAdded === 1 ? 'worker has' : 'workers have'} been added to its roster.`}{' '}
            It has the default configuration - scoring rules, tier thresholds and bid timing - which you can change from its Configuration page.
          </p>
        </div>
      ) : (
        <form id={FORM_ID} onSubmit={(event) => void handleSubmit(event)} className="bg-canvas p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name" htmlFor="new-facility-name" required>
              <input id="new-facility-name" className={INPUT_CLASS} value={form.name} onChange={(event) => update({ name: event.target.value })} required autoFocus />
            </Field>
            <Field label="Facility ID" htmlFor="new-facility-id" required hint="Letters, digits and hyphens. Your scheduling partner refers to the facility by it; it cannot be changed later.">
              <input
                id="new-facility-id"
                className={`${INPUT_CLASS} font-mono`}
                value={facilityId}
                onChange={(event) => update({ facilityIdOverride: event.target.value })}
                pattern={FACILITY_ID_PATTERN}
                required
              />
            </Field>
            <Field label="Time zone" htmlFor="new-facility-timezone" hint="The zone its days, scoring periods and bid windows are reckoned in. Not set reads as UTC." span>
              <TimeZoneSelect id="new-facility-timezone" label="Time zone" className={INPUT_CLASS} value={form.timezone} onChange={(timezone) => update({ timezone })} />
            </Field>
            <Field label="Street" htmlFor="new-facility-line1" span>
              <input id="new-facility-line1" className={INPUT_CLASS} value={form.address.line1} autoComplete="address-line1" onChange={(event) => updateAddress({ line1: event.target.value })} />
            </Field>
            <Field label="Street, line 2" htmlFor="new-facility-line2" span>
              <input id="new-facility-line2" className={INPUT_CLASS} value={form.address.line2} autoComplete="address-line2" onChange={(event) => updateAddress({ line2: event.target.value })} />
            </Field>
            <Field label="City" htmlFor="new-facility-city">
              <input id="new-facility-city" className={INPUT_CLASS} value={form.address.city} autoComplete="address-level2" onChange={(event) => updateAddress({ city: event.target.value })} />
            </Field>
            <Field label={regionLabelOf(form.address.country)} htmlFor="new-facility-region">
              <RegionInput id="new-facility-region" className={INPUT_CLASS} country={form.address.country} value={form.address.region} onChange={(region) => updateAddress({ region })} />
            </Field>
            <Field label="Postal code" htmlFor="new-facility-postal">
              <input id="new-facility-postal" className={INPUT_CLASS} value={form.address.postalCode} autoComplete="postal-code" onChange={(event) => updateAddress({ postalCode: event.target.value })} />
            </Field>
            <Field label="Country" htmlFor="new-facility-country">
              <CountryInput id="new-facility-country" className={INPUT_CLASS} value={form.address.country} onChange={(country) => updateAddress({ country })} />
            </Field>
            <Field label="Phone" htmlFor="new-facility-phone">
              <PhoneInput id="new-facility-phone" className={INPUT_CLASS} value={form.phone} maxLength={32} onChange={(phone) => update({ phone })} />
            </Field>
          </div>
          <p className="text-[11px] text-ink-mid leading-relaxed pt-3">
            The address and phone are optional now and can be added from the facility's Configuration page later; if you start the address, it must be
            complete. Only an operator can set them - SeeWhoCares cannot. The facility starts with the default configuration, ready to adjust.
          </p>
          {error && (
            <p role="alert" className="text-xs font-bold text-coral mt-3">
              {error}
            </p>
          )}
        </form>
      )}
    </Modal>
    {isAddingWorker && created && (
      <NewWorkerModal clientId={clientId} facility={created} backLabel="the new facility" onClose={() => setIsAddingWorker(false)} onCreated={() => setWorkersAdded((count) => count + 1)} />
    )}
    </>
  )
}
